import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Select,
  SettingsRow,
  SettingsSection,
  Switch,
  ViewTab,
  ViewTabList,
} from '@wundo/ui';
import {
  DEFAULT_PREP_DIGEST_MINUTES,
  PREP_DIGEST_TIME_OPTIONS,
  formatPrepDigestTime,
  type PrepDigestMoment,
} from '@wundo/shared/notificationPreferences.ts';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
  type NotificationPreferencesInput,
} from '@/lib/notificationPreferences.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/notifications')({
  component: NotificationSettingsPage,
});

const MOMENTS = [
  { value: 'evening', label: m.notifications_prep_moment_evening },
  { value: 'morning', label: m.notifications_prep_moment_morning },
] as const;

export function NotificationSettingsPage() {
  const queryClient = useQueryClient();

  const {
    data: preferences,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: fetchNotificationPreferences,
  });

  const save = useMutation({
    mutationFn: saveNotificationPreferences,
    /* The server is what decides what was stored, so the page follows it. */
    onSuccess: (saved) => queryClient.setQueryData(['notificationPreferences'], saved),
  });

  if (isPending) return null;
  if (isError) {
    return <p className='text-base text-ink-muted'>{m.notifications_load_failed()}</p>;
  }

  /* Save the whole choice every time: one PUT, one stored record, no merging. */
  const change = (patch: Partial<NotificationPreferencesInput>) =>
    save.mutate({
      prep_digest: preferences.prep_digest,
      prep_digest_moment: preferences.prep_digest_moment,
      prep_digest_minutes: preferences.prep_digest_minutes,
      summary_notifications: preferences.summary_notifications,
      ...patch,
    });

  /* Each moment has its own list of times, so the old time may not exist under the new one. */
  const changeMoment = (moment: PrepDigestMoment) =>
    change({
      prep_digest_moment: moment,
      prep_digest_minutes: DEFAULT_PREP_DIGEST_MINUTES[moment],
    });

  const timeOptions = PREP_DIGEST_TIME_OPTIONS[preferences.prep_digest_moment].map(
    (minutes) => ({ value: String(minutes), label: formatPrepDigestTime(minutes) }),
  );

  return (
    <>
      <SaveStatus pending={save.isPending} failed={save.isError} />

      <SettingsSection title={m.notifications_prep_title()}>
        <SettingsRow label={m.notifications_prep_digest()}>
          <Switch
            aria-label={m.notifications_prep_digest()}
            checked={preferences.prep_digest}
            onCheckedChange={(checked) => change({ prep_digest: checked })}
          />
        </SettingsRow>
        {preferences.prep_digest && (
          <>
            <SettingsRow label={m.notifications_prep_moment()}>
              <ViewTabList aria-label={m.notifications_prep_moment()}>
                {MOMENTS.map((option) => (
                  <ViewTab
                    key={option.value}
                    active={option.value === preferences.prep_digest_moment}
                    onClick={() => changeMoment(option.value)}
                  >
                    {option.label()}
                  </ViewTab>
                ))}
              </ViewTabList>
            </SettingsRow>
            <SettingsRow label={m.notifications_prep_time()}>
              <Select
                aria-label={m.notifications_prep_time()}
                options={timeOptions}
                value={String(preferences.prep_digest_minutes)}
                onChange={(event) =>
                  change({ prep_digest_minutes: Number(event.target.value) })
                }
              />
            </SettingsRow>
          </>
        )}
      </SettingsSection>

      <SettingsSection title={m.notifications_after_title()}>
        <SettingsRow label={m.notifications_summary()}>
          <Switch
            aria-label={m.notifications_summary()}
            checked={preferences.summary_notifications}
            onCheckedChange={(checked) => change({ summary_notifications: checked })}
          />
        </SettingsRow>
      </SettingsSection>
    </>
  );
}
